<script setup lang="ts">
import { computed, ref } from "vue";
import type { VForm } from "vuetify/components";
import dayjs from "dayjs";
import {
	maskCurrencyEur,
	unmaskCurrencyEur,
	maskInteger,
	unmaskInteger,
} from "@/modules/campaign_planner/utils/maskingUtils";
import FlightDO = Modules.CampaignPlanner.Data.FlightDO;
import SizeDO = Modules.CampaignPlanner.Data.SizeDO;

const props = defineProps<{
	flight: FlightDO;
	sizes: SizeDO[];
}>();

const analogSizes = computed(() => props.sizes);

const dateRange = computed({
	get() {
		return {
			start: props.flight.start_date ? new Date(props.flight.start_date) : null,
			end: props.flight.end_date ? new Date(props.flight.end_date) : null,
		};
	},
	set(value: { start: Date | null; end: Date | null }) {
		if (value?.start)
			props.flight.start_date = dayjs(value.start).format("YYYY-MM-DD");
		if (value?.end)
			props.flight.end_date = dayjs(value.end).format("YYYY-MM-DD");
	},
});

const showNonStandardAnalogStartDate = computed(() => {
	return (
		!props.flight.is_digital &&
		props.flight.start_date &&
		dayjs(props.flight.start_date).day() !== 0
	);
});

const showNonStandardAnalogEndDate = computed(() => {
	return (
		!props.flight.is_digital &&
		props.flight.end_date &&
		dayjs(props.flight.end_date).day() !== 6
	);
});

const showNonStandardDigitalStartDate = computed(() => {
	return (
		props.flight.is_digital &&
		props.flight.start_date &&
		dayjs(props.flight.start_date).day() !== 1
	);
});

const showNonStandardDigitalEndDate = computed(() => {
	return (
		props.flight.is_digital &&
		props.flight.end_date &&
		dayjs(props.flight.end_date).day() !== 0
	);
});

const weekdayItems = ref(["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]);

const toTimeRules = [
	() => {
		const from = props.flight.from_time_on_days;
		const to = props.flight.to_time_on_days;
		if (from && to && to <= from) return "End time must be after start time";
		return true;
	},
];

const goalTarget = computed({
	get() {
		const value = props.flight.goal_target as number | null;
		return maskInteger(value);
	},
	set(v: string) {
		props.flight.goal_target = unmaskInteger(v);
	},
});

const form = ref<InstanceType<typeof VForm>>();

const nameRules = [(v: string) => !!v?.trim() || "Flight name is required"];

const goalTargetRules = [
	() =>
		(props.flight.goal_target != null &&
			(props.flight.goal_target as number) > 0) ||
		"Must be greater than 0",
];

// Secondary optimization target (impression based only) — phrased as a
// tie-breaking rule rather than raw parameter names.
const secondaryObjectiveTypeItems = [
	{ title: "impressions", value: "audience" },
	{ title: "screens", value: "screen_count" },
	{ title: "revenue", value: "revenues" },
	{ title: "average CPM", value: "avg_cpm" },
];

const secondaryObjectiveDirectionItems = [
	{ title: "fewest", value: "minimize" },
	{ title: "most", value: "maximize" },
];

const scoreMarginPct = computed({
	get() {
		return Math.round((props.flight.primary_objective_margin ?? 0.05) * 100);
	},
	set(v: number) {
		const clamped = Math.min(100, Math.max(0, Number(v) || 0));
		props.flight.primary_objective_margin = clamped / 100;
	},
});

const scoreMarginRules = [
	(v: number) => (v >= 0 && v <= 100) || "Must be between 0 and 100",
];

defineExpose({ validate: () => form.value?.validate() });

const newTarp = ref<{ amount: number; size_id: number | null }>({
	amount: 1,
	size_id: null,
});
const addingTarp = ref(false);

const tarpHeaders = [
	{ title: "Size", key: "size_id", align: "start" },
	{ title: "Amount", key: "amount", align: "end" },
	{ title: "", key: "actions", align: "end", size: 100, sortable: false },
];

const getSizeName = (sizeId: number | null) =>
	analogSizes.value.find((s) => s.id === sizeId)?.label ?? "Unknown";

const addTarp = () => {
	if (!newTarp.value.size_id) return;
	const existing = props.flight.available_tarps.find(
		(t: any) => t.size_id === newTarp.value.size_id
	);
	if (existing) {
		existing.amount = newTarp.value.amount;
	} else {
		props.flight.available_tarps.push({ ...newTarp.value });
	}
	newTarp.value = { amount: 1, size_id: null };
	addingTarp.value = false;
};

const removeTarp = (sizeId: number) => {
	props.flight.available_tarps = props.flight.available_tarps.filter(
		(t: any) => t.size_id !== sizeId
	);
};
</script>

<template>
	<v-form ref="form">
		<v-card class="pa-5">
			<div class="section-content">
				<!--				&lt;!&ndash; Flight Name &ndash;&gt;-->
				<!--				<div class="field-group">-->
				<!--					<div class="field-label text-caption text-medium-emphasis">-->
				<!--						Flight Name-->
				<!--					</div>-->
				<!--					<v-text-field-->
				<!--						v-model="flight.name"-->
				<!--						variant="filled"-->
				<!--						density="compact"-->
				<!--						hide-details="auto"-->
				<!--						placeholder="Enter flight name…"-->
				<!--						:rules="nameRules"-->
				<!--					/>-->
				<!--				</div>-->

				<!-- Media Type -->
				<div class="field-group">
					<div class="field-label text-caption text-medium-emphasis">
						Media Type
					</div>
					<v-btn-toggle
						v-model="flight.is_digital"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
					>
						<v-btn :value="true">Digital</v-btn>
						<v-btn :value="false">Analog</v-btn>
					</v-btn-toggle>
				</div>

				<div v-if="flight.is_digital" class="field-group">
					<div class="field-label text-caption text-medium-emphasis">
						Type of Buy
					</div>
					<v-btn-toggle
						v-model="flight.type_of_buy"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
					>
						<v-btn value="ppl">Frequency (play per loop)</v-btn>
						<v-btn value="goal_impressions">Impressions</v-btn>
					</v-btn-toggle>
				</div>

				<!-- Media Pressure (digital only) -->
				<div
					v-if="flight.is_digital && flight.type_of_buy === 'ppl'"
					class="field-group"
				>
					<div class="field-label text-caption text-medium-emphasis">
						Media Pressure
					</div>
					<v-btn-toggle
						v-model="flight.slot_fraction"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
					>
						<v-btn :value="1">Full Slots</v-btn>
						<v-btn :value="0.5">Half Slots</v-btn>
						<v-btn :value="0.25">Quarter Slots</v-btn>
						<v-btn :value="0.125">Eighth Slots</v-btn>
					</v-btn-toggle>
				</div>

				<!-- Flight Goal -->
				<div class="field-group">
					<div class="field-label text-caption text-medium-emphasis">
						Optimization target
					</div>
					<v-btn-toggle
						v-model="flight.goal_name_clean"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
					>
						<v-btn value="budget">Fixed Budget</v-btn>
						<v-btn value="reach">Fixed Impressions</v-btn>
					</v-btn-toggle>
				</div>

				<!-- Goal Target -->
				<div class="field-group">
					<div class="field-label text-caption text-medium-emphasis">
						{{
							flight.goal_name_clean === "budget" ? "Budget (€)" : "Impressions"
						}}
					</div>
					<v-text-field
						v-model="goalTarget"
						type="text"
						inputmode="decimal"
						variant="filled"
						density="compact"
						hide-details="auto"
						:rules="goalTargetRules"
					/>
				</div>

				<!-- Secondary Optimization Target (impression based only), phrased as a tie-breaking rule -->
				<div
					v-if="flight.type_of_buy === 'goal_impressions'"
					class="field-group"
				>
					<div
						class="field-label text-caption text-medium-emphasis d-flex align-center gap-1"
					>
						Tie-breaking Rule
						<v-icon size="18" color="medium-emphasis" class="ml-3"
							>fa: fa-light fa-circle-question</v-icon
						>
						<v-tooltip activator="parent">
							When multiple solutions score almost equally on the primary
							optimization target, this decides which one wins. A 5% margin
							means any solution within 5% of the best score is eligible to be
							picked this way.
						</v-tooltip>
					</div>
					<div class="tie-break-sentence">
						<span class="text-body-2">When solutions score within</span>
						<v-number-input
							v-model="scoreMarginPct"
							control-variant="split"
							inset
							:min="0"
							:max="100"
							suffix="%"
							density="compact"
							hide-details="auto"
							:rules="scoreMarginRules"
							style="max-width: 140px"
						/>
						<span class="text-body-2"
							>of each other, prefer the one with the</span
						>
						<v-select
							v-model="flight.secondary_objective_direction"
							:items="secondaryObjectiveDirectionItems"
							density="compact"
							hide-details
							style="max-width: 110px"
						/>
						<v-select
							v-model="flight.secondary_objective_type"
							:items="secondaryObjectiveTypeItems"
							density="compact"
							hide-details
							style="max-width: 150px"
						/>
					</div>
				</div>

				<!-- Screen Selection Approach -->
				<div class="field-group" v-if="flight.type_of_buy === 'ppl'">
					<div class="field-label text-caption text-medium-emphasis">
						Screen Selection Approach
					</div>
					<v-btn-toggle
						v-model="flight.is_vendor_fit_enabled"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
					>
						<v-btn :value="true">Network Optimized</v-btn>
						<v-btn :value="false">Lowest CPM</v-btn>
					</v-btn-toggle>
				</div>

				<!-- Period -->
				<v-fade-transition>
					<v-alert
						color="warning"
						:icon="false"
						v-if="
							showNonStandardAnalogStartDate ||
							showNonStandardAnalogEndDate ||
							showNonStandardDigitalStartDate ||
							showNonStandardDigitalEndDate
						"
					>
						<span style="font-size: 12px">
							This is a non-standard day to
							{{
								showNonStandardAnalogStartDate ||
								showNonStandardDigitalStartDate
									? "start"
									: "end"
							}}
							a '{{
								showNonStandardAnalogStartDate || showNonStandardAnalogEndDate
									? "Analog"
									: "Digital"
							}}' campaign.</span
						>
					</v-alert>
				</v-fade-transition>
				<div class="field-group">
					<div class="field-label text-caption text-medium-emphasis">
						Period
					</div>
					<vc-date-picker
						v-model.range="dateRange"
						:columns="2"
						show-iso-weeknumbers
						mode="date"
						is-range
						:min-date="new Date()"
					>
						<template #default="{ inputValue, inputEvents }">
							<div class="d-flex gap-3 align-center">
								<v-text-field
									:value="inputValue.start"
									v-on="inputEvents.start"
									placeholder="Start"
									variant="filled"
									density="compact"
									hide-details
									readonly
								/>
								<v-icon class="mx-5" size="small" color="medium-emphasis"
									>fa: fa-light fa-arrow-right</v-icon
								>
								<v-text-field
									:value="inputValue.end"
									v-on="inputEvents.end"
									placeholder="End"
									variant="filled"
									density="compact"
									hide-details
									readonly
								/>
							</div>
						</template>
					</vc-date-picker>
				</div>

				<!-- Time & Weekdays (digital only) -->
				<template v-if="flight.is_digital">
					<div class="field-group">
						<div class="field-label text-caption text-medium-emphasis">
							Time &amp; Weekdays
						</div>
						<div class="d-flex gap-3 align-center">
							<v-text-field
								v-model="flight.from_time_on_days"
								placeholder="From"
								variant="filled"
								density="compact"
								hide-details
								style="max-width: 150px"
							/>
							<v-icon class="mx-5" size="small" color="medium-emphasis"
								>fa: fa-light fa-arrow-right</v-icon
							>
							<v-text-field
								v-model="flight.to_time_on_days"
								placeholder="To"
								variant="filled"
								density="compact"
								hide-details="auto"
								:rules="toTimeRules"
								style="max-width: 150px"
							/>
							<v-select
								class="ml-10"
								v-model="flight.weekdays"
								:items="weekdayItems"
								placeholder="Weekdays"
								variant="filled"
								density="compact"
								hide-details
								multiple
							/>
						</div>
					</div>
				</template>

				<!-- Segment Granularity -->
				<div
					class="field-group"
					v-if="flight.is_digital && flight.type_of_buy === 'ppl'"
				>
					<div
						class="field-label text-caption text-medium-emphasis d-flex align-center gap-1"
					>
						Segment Granularity
						<v-icon size="18" color="medium-emphasis" class="ml-3"
							>fa: fa-light fa-circle-question</v-icon
						>
						<v-tooltip activator="parent">
							If you choose Day, availability will be calculated separately for
							each day within the selected period.<br />
							This generally results in more available screens, as a screen only
							needs to be available for a single day instead of the entire
							period.
						</v-tooltip>
					</div>
					<v-btn-toggle
						v-model="flight.segment_granularity"
						mandatory
						density="compact"
						variant="outlined"
						color="primary"
					>
						<v-btn value="period">Period</v-btn>
						<v-btn value="day">Day</v-btn>
					</v-btn-toggle>
				</div>

				<!-- Tarps (analog only), disabled for now because NEVER been used-->
				<template v-if="!flight.is_digital && false">
					<div class="field-group field-group--inline">
						<div class="field-label text-caption text-medium-emphasis">
							Client Has Tarps
						</div>
						<v-switch
							v-model="flight.client_has_tarps_available"
							color="primary"
							density="compact"
							hide-details
							class="mt-0"
						/>
					</div>

					<template v-if="flight.client_has_tarps_available">
						<div class="field-group">
							<div class="field-label text-caption text-medium-emphasis mb-2">
								Available Tarps
							</div>

							<v-data-table
								:headers="tarpHeaders as any"
								:items="flight.available_tarps"
								density="compact"
								:items-per-page="-1"
								hide-default-footer
								class="elevation-0 border rounded"
							>
								<template v-slot:item.size_id="{ item }">
									{{ getSizeName((item as any).size_id) }}
								</template>
								<template v-slot:item.amount="{ item }">
									<div class="d-flex py-1">
										<v-spacer />
										<v-text-field
											:model-value="(item as any).amount"
											@update:model-value="(v) => (item as any).amount = Number(v)"
											type="number"
											density="compact"
											size="small"
											variant="filled"
											hide-details
											style="max-width: 100px"
										/>
									</div>
								</template>
								<template v-slot:item.actions="{ item }">
									<v-btn
										icon="fa: fa-light fa-trash-can"
										size="x-small"
										variant="text"
										color="error"
										@click="removeTarp((item as any).size_id)"
									/>
								</template>
								<template v-slot:no-data>
									<div
										class="py-3 text-center text-caption text-medium-emphasis"
									>
										No tarps configured
									</div>
								</template>
							</v-data-table>

							<div v-if="!addingTarp" class="d-flex justify-end mt-2">
								<v-btn
									size="small"
									variant="tonal"
									color="primary"
									prepend-icon="fa: fa-light fa-plus"
									@click="addingTarp = true"
								>
									Add Tarp
								</v-btn>
							</div>

							<div v-else class="d-flex gap-3 align-center mt-3">
								<v-select
									v-model="newTarp.size_id"
									:items="analogSizes"
									item-title="label"
									item-value="id"
									placeholder="Size"
									variant="filled"
									density="compact"
									hide-details
								/>
								<v-text-field
									v-model.number="newTarp.amount"
									type="number"
									placeholder="Amount"
									variant="filled"
									density="compact"
									hide-details
									style="max-width: 100px"
								/>
								<v-btn
									icon="fa: fa-light fa-check"
									size="small"
									color="primary"
									variant="tonal"
									@click="addTarp"
								/>
								<v-btn
									icon="fa: fa-light fa-xmark"
									size="small"
									variant="text"
									@click="addingTarp = false"
								/>
							</div>
						</div>
					</template>
				</template>
			</div>
		</v-card>
	</v-form>
</template>

<style scoped lang="scss">
.section-content {
	display: flex;
	flex-direction: column;
	gap: 20px;
	padding: 8px 0 16px;
}

.field-group {
	display: flex;
	flex-direction: column;
	gap: 8px;

	&--inline {
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		margin-right: 8px;
	}
}

.field-label {
	font-size: 0.75rem;
	letter-spacing: 0.03em;
	text-transform: uppercase;
}

.tie-break-sentence {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
}

:deep(.v-btn-group .v-btn) {
	font-weight: 400;
	text-transform: none;
	letter-spacing: normal;
}
</style>
